import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Button, DetailSectionTitle, Input, Label } from "@/components/ui";
import type { SettlementPayeeRow } from "@/services/adminSettlementService";
import { SettlementStepCard, type StepCardProps } from "../SettlementStepCard";
import { buildDefaultRevolutReference } from "../utils";

export function PaymentStep({
  busy,
  noSelection,
  submittingPay,
  submittingRevolut,
  payableCents,
  payee,
  blockingRevolutOpen,
  onMarkPaid,
  onRevolutPayout,
  disabled = false,
  ...card
}: StepCardProps & {
  busy: boolean;
  noSelection: boolean;
  submittingPay: boolean;
  submittingRevolut: boolean;
  payableCents: number;
  payee: SettlementPayeeRow | null;
  blockingRevolutOpen: boolean;
  onMarkPaid: (data: { reference: string; note?: string }) => void | Promise<void>;
  onRevolutPayout: (data: {
    mode: "draft";
    reference: string;
    note?: string;
  }) => void | Promise<void>;
  disabled?: boolean;
}) {
  const { t } = useTranslation("payments");
  const [paymentNote, setPaymentNote] = useState("");
  const [revolutReference, setRevolutReference] = useState(buildDefaultRevolutReference);
  const [reference, setReference] = useState("");
  const formDisabled = disabled || busy || noSelection || payableCents <= 0;
  const revolutDisabled =
    formDisabled || !payee?.has_legal_info || blockingRevolutOpen;

  const handleMarkPaid = async () => {
    if (!reference.trim()) {
      toast.error(t("settlements.detail.referenceRequired"));
      return;
    }
    await onMarkPaid({ reference: reference.trim(), note: paymentNote.trim() || undefined });
    setReference("");
    setPaymentNote("");
  };

  const handleRevolut = async () => {
    if (!revolutReference.trim()) {
      toast.error(t("settlements.detail.revolut.referenceRequired"));
      return;
    }
    if (!payee?.has_legal_info) {
      toast.error(t("settlements.detail.revolut.legalRequired"));
      return;
    }
    if (blockingRevolutOpen) {
      toast.error(t("settlements.detail.revolut.blockedActive"));
      return;
    }
    await onRevolutPayout({
      mode: "draft",
      reference: revolutReference.trim(),
      note: paymentNote.trim() || undefined,
    });
    setPaymentNote("");
  };

  return (
    <SettlementStepCard
      {...card}
      title={t("settlements.detail.steps.payTitle")}
      description={t("settlements.detail.steps.payDesc")}
      disabled={formDisabled}
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="settlement-payment-note" size="sm" spacing="tight">
            {t("settlements.detail.paymentNotePlaceholder")}
          </Label>
          <Input
            id="settlement-payment-note"
            value={paymentNote}
            onChange={(e) => setPaymentNote(e.target.value)}
            disabled={formDisabled}
          />
        </div>

        <div className="space-y-2.5 rounded-lg border border-surface-200 bg-surface-0 px-4 py-3 shadow-xs">
          <Label htmlFor="settlement-revolut-reference" size="sm" spacing="tight">
            {t("settlements.detail.revolut.referenceLabel")}
          </Label>
          <Input
            id="settlement-revolut-reference"
            value={revolutReference}
            onChange={(e) => setRevolutReference(e.target.value)}
            disabled={revolutDisabled}
            placeholder={t("settlements.detail.referencePlaceholder")}
            className="font-mono"
          />
          <Button
            type="button"
            className="w-full"
            disabled={revolutDisabled}
            isLoading={submittingRevolut}
            onClick={() => void handleRevolut()}
          >
            {t("settlements.detail.revolut.payButton")}
          </Button>
        </div>

        <div className="space-y-2.5 rounded-lg border border-surface-200 bg-surface-0 px-4 py-3 shadow-xs">
          <DetailSectionTitle>{t("settlements.detail.markPaid")}</DetailSectionTitle>
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            disabled={formDisabled}
            placeholder={t("settlements.detail.referencePlaceholder")}
            aria-label={t("settlements.detail.referencePlaceholder")}
          />
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={formDisabled}
            isLoading={submittingPay}
            onClick={() => void handleMarkPaid()}
          >
            {t("settlements.detail.markPaid")}
          </Button>
        </div>
      </div>
    </SettlementStepCard>
  );
}
